import type { ValidationErrorResult } from '@/generated-proto/com/supercell/chronos/grpc/ValidationErrorResult';

export const GAME_CONNECTION_ERROR = 'GAME_CONNECTION_ERROR';
export const REQUEST_TIMEOUT = 'REQUEST_TIMEOUT';
export const VALIDATION_ERROR = 'VALIDATION_ERROR';
export const UNKNOWN_ERROR = 'UNKNOWN_ERROR';
export const PERMISSION_ERROR = 'PERMISSION_ERROR';
export const SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE';
export const NOT_FOUND = 'NOT_FOUND';

export interface ChronosServiceError {
  errorType: string;
  message: string;
  errorName: string;
}

export class ChronosErrorBase extends Error {
  readonly errorType: string;
  readonly details?: string;
  constructor(type: string, msg?: string, details?: string) {
    super(msg);
    this.errorType = type;
    this.details = details;
  }
}

export class GenericServiceError extends ChronosErrorBase implements ChronosServiceError {
  readonly errorName = 'GenericError';
}

export class GameConnectionError extends ChronosErrorBase implements ChronosServiceError {
  readonly errorName = 'GameConnectionError';
  constructor(msg?: string) {
    super(GAME_CONNECTION_ERROR, msg);
  }
}

export class RequestTimeoutError extends ChronosErrorBase implements ChronosServiceError {
  readonly errorName = 'RequestTimeoutError';
  constructor(msg?: string) {
    super(REQUEST_TIMEOUT, msg);
  }
}

export class PermissionError extends ChronosErrorBase implements ChronosServiceError {
  readonly errorName = 'PermissionError';
  constructor(msg?: string) {
    super(PERMISSION_ERROR, msg);
  }
}

export class NotFoundError extends ChronosErrorBase implements ChronosServiceError {
  readonly errorName = 'NotFoundError';
  readonly errorType = NOT_FOUND;
  constructor(msg?: string) {
    super(NOT_FOUND, msg);
  }
}

export class ServiceUnavailableError extends ChronosErrorBase implements ChronosServiceError {
  readonly errorName = 'ServiceUnavailableError';
  constructor(msg?: string, details?: string) {
    super(SERVICE_UNAVAILABLE, msg, details);
  }
}

export class ValidationErrors extends ChronosErrorBase implements ChronosServiceError {
  readonly errorName = 'ValidationErrors';
  readonly validationErrors: ValidationErrorResult[];
  constructor(errors: ValidationErrorResult[]) {
    super(VALIDATION_ERROR, `Validation error${errors.length > 1 ? 's' : ''}`);
    this.validationErrors = errors;
  }
}

export function isChronosBaseError(error: unknown): error is ChronosErrorBase {
  if (typeof error !== 'object') {
    return false;
  }

  if (error == null) {
    return false;
  }

  if (!('errorType' in error)) {
    return false;
  }

  switch (error.errorType) {
    case GAME_CONNECTION_ERROR:
    case REQUEST_TIMEOUT:
    case VALIDATION_ERROR:
    case NOT_FOUND:
    case SERVICE_UNAVAILABLE:
      return true;
    default:
      return false;
  }
}

export const isValidationErrors = (error: unknown): error is ValidationErrors => {
  if (typeof error !== 'object' || !error) {
    return false;
  }

  return (
    'errorName' in error && error.errorName === 'ValidationErrors' && 'validationErrors' in error
  );
};
